import { useState } from "react";
import { Camera, Cpu, Loader2, ScanLine, ShieldCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import FileDropzone from "@/components/FileDropzone";

const VIN_RE = /^[A-HJ-NPR-Z0-9]{17}$/;

const FIELDS = [
  { k: "plate", label: "Targa (A)", mono: true },
  { k: "vin", label: "Telaio / VIN (E)", mono: true, wide: true },
  { k: "make", label: "Marca (D.1)" },
  { k: "model", label: "Modello (D.3)" },
  { k: "year", label: "Anno immatr. (B)", mono: true },
  { k: "displacement", label: "Cilindrata cm³ (P.1)", mono: true },
  { k: "power_kw", label: "Potenza kW (P.2)", mono: true },
  { k: "fuel", label: "Carburante (P.3)" },
  { k: "engine_code", label: "Codice Motore (P.5)", mono: true },
  { k: "engine", label: "Motorizzazione", wide: true },
];

function VehicleForm({ vehicle, setVehicle, aiKeys }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-5" data-testid="vehicle-form">
      {FIELDS.map((f) => (
        <div key={f.k} className={f.wide ? "col-span-2" : ""}>
          <label className="label">{f.label}</label>
          <input
            className={`field ${f.mono ? "mono uppercase" : ""} ${aiKeys.has(f.k) ? "ai-filled" : ""}`}
            value={vehicle[f.k] || ""}
            data-testid={`vehicle-${f.k.replace("_", "-")}-input`}
            onChange={(e) => setVehicle({ ...vehicle, [f.k]: e.target.value })}
          />
        </div>
      ))}
    </div>
  );
}

function ScanPanel({ onResult }) {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const scan = async ([file]) => {
    if (!/image\/(jpeg|png|webp)/.test(file.type)) {
      toast.error("Carica una foto JPG, PNG o WEBP");
      return;
    }
    setPreview(URL.createObjectURL(file));
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/ai/scan-libretto", fd);
      onResult(data, "libretto");
      toast.success("Libretto letto: verifica i dati estratti");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="grid sm:grid-cols-[1fr_200px] gap-4 items-stretch">
      <FileDropzone testid="libretto-dropzone" accept="image/jpeg,image/png,image/webp" multiple={false} onFiles={scan}
        title="Carica / Scatta foto Libretto" hint="Foto nitida e ben illuminata della Carta di Circolazione" capture="environment" />
      <div className="surface-2 grid place-items-center overflow-hidden min-h-[140px] relative">
        {preview ? <img src={preview} alt="Libretto" className="w-full h-full object-cover" /> : <Camera className="text-slate-600" size={32} />}
        {busy && (
          <div className="absolute inset-0 bg-black/75 grid place-items-center text-sm" data-testid="libretto-scanning">
            <div className="flex flex-col items-center gap-2"><Loader2 className="animate-spin text-[#E50914]" /> Lettura AI in corso…</div>
          </div>
        )}
      </div>
    </div>
  );
}

function VinPanel({ vehicle, setVehicle, onResult }) {
  const [busy, setBusy] = useState(false);
  const vin = (vehicle.vin || "").toUpperCase().replace(/\s/g, "");
  const valid = VIN_RE.test(vin);
  const bad = vin.match(/[IOQ]/g);
  const decode = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ai/decode-vin", { vin });
      onResult(data, "vin");
      toast.success("VIN decodificato: verifica i dati");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end">
      <div>
        <label className="label">VIN — 17 caratteri</label>
        <input className="field mono uppercase text-lg tracking-[0.2em]" maxLength={17} value={vin}
          placeholder="WVWZZZ1KZ6W000000" data-testid="vin-input"
          onChange={(e) => setVehicle({ ...vehicle, vin: e.target.value.toUpperCase().replace(/\s/g, "") })} />
        <div className="flex items-center gap-2 mt-2 text-xs mono" data-testid="vin-validation">
          {valid ? (
            <span className="text-green-400 flex items-center gap-1"><ShieldCheck size={14} /> Formato VIN valido</span>
          ) : (
            <span className="text-amber-400 flex items-center gap-1">
              <AlertTriangle size={14} /> {vin.length}/17 {bad ? `· caratteri non ammessi: ${[...new Set(bad)].join(", ")}` : ""}
            </span>
          )}
        </div>
      </div>
      <button type="button" onClick={decode} disabled={!valid || busy} className="btn-red px-5 h-[46px] flex items-center gap-2"
        data-testid="decode-vin-button">
        {busy ? <Loader2 size={16} className="animate-spin" /> : <Cpu size={16} />} Decodifica con AI
      </button>
    </div>
  );
}

export default function VehicleIdentifier({ vehicle, setVehicle }) {
  const [mode, setMode] = useState("scan");
  const [aiKeys, setAiKeys] = useState(new Set());
  const [notes, setNotes] = useState("");
  const onResult = (data, src) => {
    const keys = new Set();
    const next = { ...vehicle };
    FIELDS.forEach(({ k }) => {
      if (data[k]) {
        next[k] = data[k];
        keys.add(k);
      }
    });
    setVehicle(next);
    setAiKeys(keys);
    setNotes([data.confidence && `Affidabilità ${src === "vin" ? "decodifica" : "lettura"}: ${data.confidence}`, data.notes].filter(Boolean).join(" — "));
  };
  const tab = (id, Icon, label) => (
    <button type="button" onClick={() => setMode(id)} data-testid={`vehicle-mode-${id}`}
      className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-semibold border-b-2 transition-colors ${
        mode === id ? "border-[#E50914] text-white" : "border-transparent text-slate-400 hover:text-white"}`}>
      <Icon size={16} /> {label}
    </button>
  );
  return (
    <div>
      <div className="flex border-b border-[#383838] mb-5">
        {tab("scan", ScanLine, "A · Scansione AI Libretto")}
        {tab("vin", Cpu, "B · Inserimento VIN / Targa")}
      </div>
      {mode === "scan" ? <ScanPanel onResult={onResult} /> : <VinPanel vehicle={vehicle} setVehicle={setVehicle} onResult={onResult} />}
      {notes && <div className="mt-4 text-xs text-slate-400 surface-2 px-3 py-2" data-testid="ai-notes">{notes}</div>}
      <VehicleForm vehicle={vehicle} setVehicle={setVehicle} aiKeys={aiKeys} />
      <p className="text-xs text-slate-500 mt-3">I campi evidenziati in rosso sono stati compilati dall'AI: controllali e correggili se necessario.</p>
    </div>
  );
}
